import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import { editorState } from './editor-state';

const ChatUI = ({
  messages,
  input,
  setInput,
  isLoading,
  expandedMessages,
  setExpandedMessages,
  onSubmit,
  onToggleExpand,
  specification
}) => {
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [uploadedImages, setUploadedImages] = useState([]);

  // Force scroll to bottom function
  const scrollToBottom = () => {
    // Use multiple methods to ensure scrolling works
    if (messagesEndRef.current) {
      // Method 1: Direct scroll on the div
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

      // Method 2: Scroll the parent container
      const scrollContainer = messagesEndRef.current.parentElement?.parentElement;
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }

      // Method 3: Find the scrollable element by class
      const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }
  };

  // Scroll whenever messages change with a slight delay
  useEffect(() => {
    // Immediate scroll
    scrollToBottom();

    // Delayed scroll to catch any animations or DOM updates
    const timeouts = [
      setTimeout(scrollToBottom, 50),
      setTimeout(scrollToBottom, 150),
      setTimeout(scrollToBottom, 300)
    ];

    return () => timeouts.forEach(t => clearTimeout(t));
  }, [messages.length]); // Use messages.length to ensure it triggers on any change

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          src: e.target.result,
          file: file
        };
        setUploadedImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle paste for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const newImage = {
                id: Date.now() + Math.random(),
                src: event.target.result,
                file: file
              };
              setUploadedImages(prev => [...prev, newImage]);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    }
  };

  // Remove uploaded image
  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Handle input change with auto-resize
  const handleInputChange = (e) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 480); // Max 20 lines * 24px
    textarea.style.height = newHeight + 'px';
    
    // Show scrollbar if content exceeds 20 lines
    if (textarea.scrollHeight > 480) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  // Enhanced submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      // Pass images to the submit handler
      const imagesToSend = [...uploadedImages];
      
      // Clear images after submit
      setUploadedImages([]);
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '24px';
      }
      
      onSubmit(e, imagesToSend);
    }
  };

  const ColorBox = ({ color }) => {
    const colorClasses = {
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500'
    };

    return (
      <div className={`w-12 h-12 ${colorClasses[color]} rounded`} />
    );
  };

  //console.log('ChatUI component rendered with messages:', JSON.stringify(messages, null, 2));

  return (
    <div className="w-full h-full flex flex-col relative">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4 pb-32">
          {messages.map((message, index) => {
            if (message.isColorBox) {
              return (
                <div key={index} className="flex justify-start">
                  <ColorBox color={message.color} />
                </div>
              );
            }

            const isExpanded = !!expandedMessages[index];
            const hasDetail = message.detail && message.detail.trim() !== '';

            return (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md rounded-lg px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {/* Render uploaded images */}
                  {message.images && message.images.length > 0 && (
                    <>
                      //{console.log('Rendering images for message:', message.images)}
                      {message.images.map((imageSrc, imgIndex) => (
                        <img 
                          key={imgIndex}
                          src={imageSrc} 
                          alt={`Uploaded ${imgIndex + 1}`} 
                          className="w-full h-auto rounded mb-2 max-h-48 object-cover"
                        />
                      ))}
                    </>
                  )}
                  
                  <div className="flex items-start">
                    {hasDetail && (
                      <button
                        onClick={() => onToggleExpand(index)}
                        className={`mr-2 p-0 border-none bg-transparent cursor-pointer focus:outline-none flex items-center ${
                          message.role === 'user' 
                            ? 'text-white hover:text-gray-200' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={{ alignSelf: 'center', lineHeight: 'inherit' }}
                        aria-expanded={isExpanded}
                        aria-controls={`detail-${index}-${message.role}`}
                        title={isExpanded ? "Collapse details" : "Expand details"}
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    )}
                    <div className="whitespace-pre-wrap flex-1 overflow-hidden">
                      {message.content}
                      {message.linkDetails && message.linkDetails.action === 'selectHistoryVersion' && (
                        <button
                          onClick={() => editorState.selectHistoryVersion(message.linkDetails.arg)}
                          className="ml-1 underline cursor-pointer text-blue-600 hover:text-blue-800 focus:outline-none"
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            padding: 0,
                            display: 'inline',
                            textAlign: 'left',
                          }}
                          title={`Go to version ${message.linkDetails.arg}`}
                        >
                          {message.linkDetails.text}
                        </button>
                      )}
                    </div>
                  </div>
                  {hasDetail && (
                    <div
                      id={`detail-${index}-${message.role}`}
                      className="overflow-hidden"
                      style={{
                        maxHeight: isExpanded ? '100%' : '0px',
                        borderTopStyle: 'solid',
                        borderTopWidth: isExpanded ? '1px' : '0px',
                        borderTopColor: message.role === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                        transition: 'max-height 0.3s ease-in-out, margin-top 0.3s ease-in-out, padding-top 0.3s ease-in-out, border-top-width 0.3s ease-in-out',
                        marginTop: isExpanded ? '0.5rem' : '0px',
                        paddingTop: isExpanded ? '0.5rem' : '0px',
                      }}
                    >
                      {isExpanded && (
                        <div className="whitespace-pre-wrap text-sm">
                          {message.detail}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 rounded-lg px-4 py-2 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Thinking...
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - floating at bottom of panel */}
      <div className="absolute bottom-4 left-4 right-4">
        <div className="w-full max-w-[750px] mx-auto">
          <div className="bg-white border border-gray-200 rounded-3xl shadow-lg">
            {/* Text Input - Top part */}
            <div className="px-4 pt-3 pb-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                onPaste={handlePaste}
                placeholder={specification ? "How should I change the component?" : "Specify the new component..."}
                disabled={isLoading}
                className="w-full resize-none bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-base leading-6 min-h-[24px]"
                rows={1}
                style={{ 
                  height: '24px',
                  maxHeight: '480px',
                  overflowY: input.split('\n').length > 20 ? 'auto' : 'hidden'
                }}
              />
            </div>

            {/* Bottom Toolbar */}
            <div className="px-4 py-2.5 flex items-center justify-between border-t border-gray-100">
              {/* Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                disabled={isLoading}
              >
                <Plus className="w-5 h-5" />
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />

              {/* Send Button */}
              <button
                onClick={handleSubmit}
                disabled={isLoading || !input.trim()}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>

            {/* Image Preview - Below the toolbar */}
            {uploadedImages.length > 0 && (
              <div className="px-4 pb-3 pt-2">
                <div className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 py-2">
                  {uploadedImages.map((image) => (
                    <div key={image.id} className="relative flex-shrink-0 group">
                      <img 
                        src={image.src} 
                        alt="Preview" 
                        className="w-16 h-16 object-cover rounded border"
                      />
                      <button
                        onClick={() => removeImage(image.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatUI;